import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import isEqual from 'lodash-es/isEqual';
import OkCancelModal from '@/shared/components/OkCancelModal';
import { CanCreate } from '@/shared/components/security';
import { injectStateService, StateService } from '@/shared/services/StateService';

import intlShape from '@/shared/utils/intlShape';
import ToastrService from '@/shared/services/ToastrService';
import moment from 'moment';
import classNames from 'classnames';
import DatePicker from '@/shared/components/input/DatePicker';
import Checkbox from '@/shared/components/input/Checkbox';
import { AppointmentIcon, NoteIcon, PhoneIcon } from '@/shared/components/Icons';
import FileUpload from '@/shared/components/input/FileUpload';
import InlineAttachment from '@/shared/components/InlineAttachment';
import api from '@/shared/api';
import config from '@/config';
import handleHttpError from '@/shared/services/HandleHttpError';
import TimeInput from '@/shared/components/input/TimeInput';
import { isBeforeMinimum, isEmptyHtmlField } from '@/shared/utils/validationHelpers';
import RichTextEditor from '@/shared/components/RichTextEditor';
import {
  createEmptyRegistration,
  executeAction,
  isRegistrationValid,
} from '@/shared/components/registration/registrationHelpers';
import RegistrationComponent from '@/shared/components/registration/RegistrationComponent';
import messages from './interactionMessages';
import InteractionTagMultiSelect from './InteractionTagMultiSelect';
import CanRead from '@/shared/components/security/CanRead';
import { LinkIcon } from '@/shared/components/Icons';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import DataCacheService from '@/shared/services/DataCacheService';
import { FolderOpenIcon } from '@/shared/components/Icons';
import DocumentSelectorModal from '@/shared/components/DocumentSelectorModal';
import Button from '@/shared/components/Button';

const registrationOptions = {
  showDate: false,
  showRelation: false,
  editableRelation: true,
  showInteraction: false,
  showPrice: false,
  showDiscount: false,
  showVat: false,
  showTotalAmount: false,
  showBillable: false,
  showInForfait: false,
  showAssignments: true,
};

class CreateInteractionModal extends Component {
  static propTypes = {
    onSave: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
    onClose: PropTypes.func.isRequired,
    interactionType: PropTypes.number.isRequired,
    relationId: PropTypes.string.isRequired,
    documentUploadEndpoint: PropTypes.string.isRequired,
    stateService: PropTypes.instanceOf(StateService).isRequired,
  };

  constructor(props) {
    super(props);
    this.RegistrationComponent = React.createRef();
    this.state = {
      interaction: {
        interactionType: props.interactionType,
        title: '',
        description: '',
        date: moment(),
        minutes: moment.duration(moment().format('HH:mm')).asMinutes(),
        attachments: [],
        showDocumentSelectorModal: false,
      },
      showRegistrationForm: false,
      saving: false,
      showValidation: false,
      registration: createEmptyRegistration({
        type: 0,
        source: 4,
      }),
      templates: [],
      initialDescription: null,
      tags: [],
      priceLoading: false,
    };
  }
  componentDidMount = () => {
    const { relationId, interactionType } = this.props;
    const relationNamePromise = api.base.post(`${config.appUrls.api}/api/relation/names`, [
      relationId,
    ]);

    const interactionTemplatePromise = api.base.get(
      `${config.appUrls.api}/api/interactiontemplate/company/${interactionType}`,
    );

    const tagsPromise = api.base.get(`${config.appUrls.interactions}/api/tags`);

    Promise.all([relationNamePromise, interactionTemplatePromise, tagsPromise]).then(
      ([{ data: relationData }, { data: templateData }, { data: tagsData }]) => {
        const [relation] = relationData;
        this.setState(({ registration }) => ({
          registration: executeAction(registration, 'relationChanged', relation),
        }));
        this.setState({ templates: templateData, selectedTemplate: undefined, tags: tagsData });
      },
    );
  };

  getInteractionLabel = () => {
    const { intl, interactionType } = this.props;

    switch (interactionType) {
      case 0:
        return intl.formatMessage(messages.NewNote);
      case 1:
        return intl.formatMessage(messages.NewPhoneCall);
      case 3:
        return intl.formatMessage(messages.NewAppointment);
      default:
        return intl.formatMessage(messages.Note);
    }
  };

  getInteractionIcon = () => {
    const { interactionType } = this.props;

    switch (interactionType) {
      case 0:
        return <NoteIcon tooltip={messages.Note} />;
      case 1:
        return <PhoneIcon tooltip={messages.PhoneCall} />;
      case 3:
        return <AppointmentIcon tooltip={messages.Appointment} />;
      default:
        return <NoteIcon tooltip={messages.Note} />;
    }
  };

  navigateToTemplates = () => {
    const { stateService, interactionType } = this.props;
    let goToTab = 0;
    switch (interactionType) {
      case 0:
        goToTab = 'NoteTemplates';
        break;
      case 3:
        goToTab = 'MeetingTemplates';
        break;
      case 1:
        goToTab = 'PhoneTemplates';
        break;
      default:
        break;
    }
    stateService.gotoState('settings.interactiontemplates', { tab: goToTab });
  };

  titleChanged = value => {
    this.setState(state => ({
      ...state,
      interaction: {
        ...state.interaction,
        title: value,
      },
    }));
  };

  descriptionChanged = value => {
    this.setState(state => ({
      ...state,
      interaction: {
        ...state.interaction,
        description: value,
      },
    }));
  };
  doRegistrationAction = (action, value) => {
    this.setState(({ registration }) => {
      const newValue = executeAction(registration, action, value);
      if (isEqual(newValue, registration)) return {};
      return { registration: newValue };
    });
  };
  saveInteraction = () => {
    const { onSave, intl } = this.props;
    const { interaction, showRegistrationForm, registration } = this.state;

    if (!interaction.date) {
      ToastrService.error(intl.formatMessage(messages.DateMandatory));
      return;
    }

    if (isEmptyHtmlField(interaction.title) && isEmptyHtmlField(interaction.description)) {
      ToastrService.error(intl.formatMessage(messages.TitleOrDescriptionMandatory));
      return;
    }

    const time = moment.duration(interaction.minutes, 'minutes');
    const interactionDate = moment.utc(interaction.date);
    const newDateTime = moment([
      interactionDate.year(),
      interactionDate.month(),
      interactionDate.date(),
      time.hours(),
      time.minutes(),
      0,
      0,
    ]);

    if (!showRegistrationForm) {
      const model = {
        ...interaction,
        date: newDateTime,
      };
      return onSave(model);
    }

    const canSaveRegistration = isRegistrationValid(registration, registrationOptions);

    if (canSaveRegistration) {
      this.setState({ saving: true });
      return api.base
        .post(`${config.appUrls.api}/api/Registration/POST`, [registration])
        .then(
          ({ data }) => {
            const [saveRegistration = null] = data;
            if (saveRegistration) {
              const formattedDuration = moment
                .duration(registration.durationMinutes, 'minutes')
                .format('hh:mm', {
                  trim: false,
                });

              // Cant access registration on state anymore here for some reason
              const content = `${formattedDuration} ${(registration.resource &&
                registration.resource.label) ||
                ''}`;
              ToastrService.info(content, intl.formatMessage(messages.Registered));

              const model = {
                ...interaction,
                objectId: saveRegistration.group,
                objectType: 1,
                date: newDateTime,
              };
              return onSave(model);
            }
          },
          error => {
            handleHttpError(intl)(error);
          },
        )
        .finally(() => this.setState({ saving: true }));
    }
  };

  onDocumentUploaded = (error, attachment) => {
    const { intl } = this.props;
    if (error) {
      ToastrService.error(intl.formatMessage(messages.MsgEmailAttachmentUploadFailed));
      return;
    }
    const { interaction } = this.state;

    interaction.attachments = [...interaction.attachments, attachment];
    this.setState({
      interaction,
    });
  };

  deleteAttachment = attachment => {
    const { interaction } = this.state;
    interaction.attachments = interaction.attachments.filter(x => x.id !== attachment.id);

    this.setState({
      interaction,
    });
  };

  tagsChanged = tags => {
    this.setState(({ interaction }) => ({ interaction: { ...interaction, tags } }));
  };
  dateChanged = date => {
    this.setState(({ interaction, registration }) => ({
      interaction: {
        ...interaction,
        date,
      },
      registration: executeAction(registration, 'dateChanged', date),
    }));
  };

  onCancel = () => {
    const { onClose } = this.props;
    onClose();
  };

  showValidation = () => this.setState({ showValidation: true });
  isFormInvalid = () => {
    const { registration, showRegistrationForm, interaction } = this.state;
    if (isEmptyHtmlField(interaction.title) && isEmptyHtmlField(interaction.description))
      return true;
    if (!interaction.date || !moment(interaction.date).isValid()) return true;

    if (showRegistrationForm && !isRegistrationValid(registration, registrationOptions))
      return true;
    return false;
  };

  templateChanged = event => {
    const template = event.target.value;

    this.setState(
      state => ({
        ...state,
        selectedTemplate: template,
        loading: !!template,
      }),
      () => this.retrieveTemplatedInteraction(),
    );
  };

  retrieveTemplatedInteraction = () => {
    const { intl } = this.props;
    const { selectedTemplate, interaction, tags } = this.state;
    const employee = DataCacheService.getDataCache().employee;
    api.base
      .post(`${config.appUrls.api}/api/interactiontemplate/GetInteractionTemplate`, {
        templateId: selectedTemplate,
        language: employee.userLanguage,
      })
      .then(({ data }) => {
        const {
          templateLanguageId,
          templateNotFound,
          templateNotAvailableInRelationLanguage,
        } = data;

        // if we got a language id, this means the template was found and available in the relation language
        if (templateLanguageId) {
          if (!!data.title) {
            this.titleChanged(data.title);
          }
          if (!!data.body) {
            this.setState({ initialDescription: data.body });
          }
          if (!!data.interactionTagIds && data.interactionTagIds.length > 0) {
            const interactionTags = tags
              .filter(t => data.interactionTagIds.includes(t.id))
              .map(t => t.name);
            this.tagsChanged(interactionTags);
          }
        } else {
          if (templateNotFound) {
            ToastrService.error(intl.formatMessage(messages.interactiontemplateNotFound));
          }
          if (templateNotAvailableInRelationLanguage) {
            ToastrService.error(intl.formatMessage(messages.msgInteractionTemplateNotInLanguage));
          }
          this.setState({ selectedTemplate: undefined, initialDescription: '' });
        }
      });
  };

  render() {
    const { intl, documentUploadEndpoint, stateService, relationId } = this.props;
    const {
      interaction,
      dateFocused,
      showRegistrationForm,
      saving,
      registration,
      showValidation,
      templates,
      selectedTemplate,
      initialDescription,
      showDocumentSelectorModal,
      priceLoading,
    } = this.state;

    const titleClassName = classNames('form-group', {
      'has-error':
        showValidation &&
        isEmptyHtmlField(interaction.title) &&
        isEmptyHtmlField(interaction.description),
    });
    const descriptionClassName = classNames('form-group', {
      'has-error':
        showValidation &&
        isEmptyHtmlField(interaction.title) &&
        isEmptyHtmlField(interaction.description),
    });
    const dateClassName = classNames('form-group', {
      'has-error': showValidation && !interaction.date,
    });

    return (
      <OkCancelModal
        size="lg"
        title={
          <span>
            {this.getInteractionIcon()}
            <span className="margin10L">{this.getInteractionLabel()}</span>
          </span>
        }
        show
        onClose={this.onCancel}
        onSave={this.saveInteraction}
        onHoverSave={this.showValidation}
        disabled={saving || priceLoading || (showValidation && this.isFormInvalid())}
      >
        <form ref={this.form}>
          <div className="form-horizontal form-labels-left container-fluid">
            <div className="form-group">
              <label className="control-label col-sm-2">
                {intl.formatMessage(messages.Template)}
              </label>
              <div className="col-md-6">
                {!templates.length && (
                  <label className="control-label">
                    {intl.formatMessage(messages.NoTemplatesFound)}
                  </label>
                )}
                {templates.length > 0 && (
                  <select
                    onChange={this.templateChanged}
                    className="form-control"
                    value={selectedTemplate || intl.formatMessage(messages.ChooseTemplate)}
                  >
                    {!selectedTemplate && (
                      <option value="">{intl.formatMessage(messages.ChooseTemplate)}</option>
                    )}
                    {templates
                      .map(template => ({
                        ...template,
                        translation: intl.formatMessage(
                          listItemMsg('template', template.id, template.name),
                        ),
                      }))
                      .sort((a, b) =>
                        a.translation.toLowerCase().localeCompare(b.translation.toLowerCase()),
                      )
                      .map(template => (
                        <option key={template.id} value={template.id}>
                          {template.translation}
                        </option>
                      ))}
                  </select>
                )}
              </div>
              <span className="col-md-2">
                <CanRead on="settings">
                  <button
                    type="button"
                    className="btn btn-default"
                    title={intl.formatMessage(messages.OpenInteractionTemplates)}
                    onClick={() => this.navigateToTemplates()}
                  >
                    <LinkIcon className="margin5R" />
                    <FormattedMessage {...messages.Templates} />
                  </button>
                </CanRead>
              </span>
            </div>

            <div className={dateClassName}>
              <label className="control-label col-sm-2">{intl.formatMessage(messages.Date)}</label>
              <div className="col-sm-8">
                <div style={{ display: 'flex' }}>
                  <DatePicker
                    placeholder={intl.formatMessage(messages.Date)}
                    date={interaction.date}
                    onDateChange={this.dateChanged}
                    focused={dateFocused}
                    onFocusChange={({ focused }) => this.setState({ dateFocused: focused })}
                    id="interactionDate"
                    required
                    block
                    small
                    isOutsideRange={isBeforeMinimum}
                  />
                  <div className="margin5L" style={{ width: '135px' }}>
                    <TimeInput
                      value={interaction.minutes}
                      id="interactionMinutes"
                      onChange={value =>
                        this.setState(state => ({
                          ...state,
                          interaction: {
                            ...state.interaction,
                            minutes: value,
                          },
                        }))
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className={titleClassName}>
              <label className="control-label col-sm-2">{intl.formatMessage(messages.Title)}</label>
              <div className="col-sm-10">
                <input
                  type="text"
                  autoFocus
                  value={interaction.title}
                  placeholder={intl.formatMessage(messages.Title)}
                  onChange={e => this.titleChanged(e.target.value)}
                  required
                  maxLength={500}
                  className="form-control"
                />
              </div>
            </div>
            <div className={descriptionClassName}>
              <label className="control-label col-sm-2">
                {intl.formatMessage(messages.Description)}
              </label>
              <div className="col-sm-10">
                <RichTextEditor
                  onChange={newContent => this.descriptionChanged(newContent)}
                  base64Images
                  id="new"
                  initialValue={initialDescription}
                />
              </div>
            </div>
            <CanCreate on="documents">
              <div className="form-group">
                <label className="control-label col-sm-2">
                  {intl.formatMessage(messages.Attachments)}
                </label>
                <div className="col-sm-10">
                  {interaction.attachments && (
                    <div>
                      {interaction.attachments.map(attachment => (
                        <InlineAttachment
                          key={attachment.id}
                          attachment={attachment}
                          onDelete={this.deleteAttachment}
                        />
                      ))}
                    </div>
                  )}
                  <div className="file-upload-wrapper">
                    <FileUpload
                      endpoint={documentUploadEndpoint}
                      onFileUploaded={(error, file) => this.onDocumentUploaded(error, file)}
                      removeSuccessfullAfter={5000}
                    />
                  </div>
                  <CanRead on="Documents">
                    <Button
                      btnClassName="btn-block margin10T"
                      onClick={() => this.setState({ showDocumentSelectorModal: true })}
                    >
                      <FolderOpenIcon className="margin5R" />
                      <FormattedMessage {...messages.ChooseDocumentInAdminPulse} />
                    </Button>
                  </CanRead>
                </div>
              </div>
            </CanCreate>
            <div className="form-group">
              <label className="control-label col-sm-2">{intl.formatMessage(messages.Tags)}</label>
              <div className="col-sm-10">
                <InteractionTagMultiSelect
                  value={interaction.tags}
                  onChange={this.tagsChanged}
                  allowNew
                  idProp="label"
                  caseSensitive={false}
                />
              </div>
            </div>
            <div className="form-group">
              <div className="col-sm-offset-2 col-sm-10">
                <Checkbox
                  value={showRegistrationForm}
                  text={intl.formatMessage(messages.CreateTimeRegistration)}
                  onClick={() =>
                    this.setState(state => ({
                      ...state,
                      showRegistrationForm: !state.showRegistrationForm,
                    }))
                  }
                />
              </div>
            </div>
            {showRegistrationForm && (
              <RegistrationComponent
                registration={registration}
                onAction={this.doRegistrationAction}
                onPriceLoadingChanged={loading => this.setState({ priceLoading: loading })}
                options={registrationOptions}
                showValidation={showValidation}
              />
            )}
            {showDocumentSelectorModal && (
              <DocumentSelectorModal
                show
                initialRelation={relationId}
                onDocumentsSelected={documents => {
                  this.setState({ showDocumentSelectorModal: false }, () =>
                    documents.map(x => this.onDocumentUploaded(null, x)),
                  );
                }}
                onClose={() => this.setState({ showDocumentSelectorModal: false })}
              />
            )}
          </div>
        </form>
      </OkCancelModal>
    );
  }
}

export default injectIntl(CreateInteractionModal);
